import { Component, OnInit } from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

// tslint:disable:nx-enforce-module-boundaries
import { AuthService } from '@elight/client/shared/utility/auth';
import {
    AbstractBaseComponent,
    FormatFunctions,
    matchValueValidator,
    NotificationService,
} from '@elight/client/shared/utility/common';
// tslint:enable:nx-enforce-module-boundaries
import { UserService } from '@elight/client/shared/data-access/users';

@Component({
    selector: 'elight-register-page',
    templateUrl: './register-page.component.html',
    styleUrls: ['./register-page.component.scss'],
})
export class RegisterPageComponent
    extends AbstractBaseComponent
    implements OnInit {
    public registerForm: FormGroup;
    public hasError = false;
    public inviteError = false;
    public inviteErrorMessage = '';
    public inProgress = true;
    public userExists = false;
    public loading = true;

    private readonly inviteCode: string;

    constructor(
        private authService: AuthService,
        private userService: UserService,
        private route: ActivatedRoute,
        private router: Router,
        private notificationService: NotificationService,
        fb: FormBuilder
    ) {
        super();

        this.inviteCode = this.route?.snapshot?.queryParams?.code;
        if (!this.inviteCode) {
            this.router.navigate(['auth/login']);
        }

        const passwordControl = new FormControl('', [
            Validators.required,
            Validators.minLength(8),
        ]);

        this.registerForm = fb.group({
            displayName: fb.control('', [Validators.required]),
            password: passwordControl,
            passwordConfirm: fb.control('', [
                Validators.required,
                matchValueValidator(passwordControl),
            ]),
        });
    }

    ngOnInit(): void {
        this.userService
            .getInviteByCode(this.inviteCode)
            .pipe(
                switchMap((invite: any) => {
                    if ('ACCEPTED' === invite.status) {
                        this.inviteError = true;
                        this.inviteErrorMessage =
                            'The invite has already been accepted';
                        this.loading = false;
                        this.inProgress = false;

                        return EMPTY;
                    } else {
                        return this.userService.checkUserExistsByEmail(
                            invite.email
                        );
                    }
                })
            )
            .subscribe(
                (userExists: boolean) => {
                    this.inProgress = false;
                    this.loading = false;
                    this.userExists = userExists;
                },
                () => {
                    this.inviteError = true;
                    this.inviteErrorMessage = "The invite doesn't exist";
                    this.loading = false;
                    this.inProgress = false;
                }
            );
    }

    onRegisterFormSubmit() {
        if (this.userExists) {
            this.inProgress = true;
            this.authService
                .acceptInviteExistingUser({ code: this.inviteCode })
                .subscribe((res) => {
                    this.notificationService.show('Sign Up Successful');
                    this.router.navigate(['projects']);
                    this.inProgress = false;
                });
        } else {
            if (this.registerForm.valid) {
                const password = this.registerForm?.value?.password;
                const displayName = FormatFunctions.formatName(
                    this.registerForm?.value?.displayName
                );
                this.inProgress = true;
                this.authService
                    .acceptInvite({
                        code: this.inviteCode,
                        password,
                        displayName,
                    })
                    .pipe(
                        switchMap(({ email }: any) =>
                            this.authService.login({
                                email,
                                password,
                            })
                        ),
                        finalize(() => {
                            this.inProgress = false;
                        })
                    )
                    .subscribe((res) => {
                        this.notificationService.show('Sign Up Successful');
                        this.router.navigate(['projects']);
                    });
            }
        }
    }

    navigateToLogin() {
        this.router.navigate(['auth/login']);
    }
}
